import { useState } from "react";

const photoLocations = [
  { name: "CVS Pharmacy — Ocoee", address: "1566 E Silver Star Rd, Ocoee, FL 34761", hours: "Daily 7 AM – 11 PM" },
  { name: "CVS Pharmacy — Winter Garden", address: "13750 W Colonial Dr, Winter Garden, FL 34787", hours: "Daily 7 AM – 11 PM" },
  { name: "CVS Pharmacy — Dr. Phillips", address: "7780 W Sand Lake Rd, Orlando, FL 32819", hours: "Daily 7 AM – 11 PM" },
  { name: "Walgreens — Ocoee", address: "2677 E McKey St, Ocoee, FL 34761", hours: "Call for hours" },
  { name: "Walgreens — Winter Garden", address: "15607 W Colonial Dr, Winter Garden, FL 34787", hours: "Call for hours" },
  { name: "The UPS Store — Ocoee", address: "1560 E Silver Star Rd, Ocoee, FL 34761", hours: "Call for hours" },
  { name: "The UPS Store — Winter Garden", address: "303 S Dillard St, Winter Garden, FL 34787", hours: "Call for hours" },
  { name: "FedEx Office — Orlando — I-Drive", address: "7319 W Sand Lake Rd, Orlando, FL 32819", hours: "Call for hours" },
];

const dmvLocations = [
  {
    name: "Orange County Tax Collector — Ocoee",
    address: "1800 E Silver Star Rd, Ocoee, FL 34761",
    hours: "Mon–Fri, 8:30 AM – 5:00 PM",
    note: "Closest to Windermere — western Orange County option. Appointment recommended.",
    featured: true,
  },
  {
    name: "Orange County Tax Collector — Winter Garden",
    address: "14035 W Colonial Dr, Winter Garden, FL 34787",
    hours: "Mon–Fri, 8:30 AM – 5:00 PM",
    note: "Western Orange County option near Winter Garden Village. Appointment recommended.",
    featured: false,
  },
  {
    name: "Orange County Tax Collector — Downtown Orlando",
    address: "200 S Orange Ave, Orlando, FL 32801",
    hours: "Mon–Fri, 8:30 AM – 5:00 PM",
    note: "Downtown Orlando location. Driver licenses, REAL ID, titles & registration. Appointment recommended.",
    featured: false,
  },
];

export default function WindermereHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 6);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Windermere Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local Windermere and Orange County resources to help you gather everything you need.
          </p>
        </div>

        <div className="space-y-6">

          {/* Passport Photos */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations Near Windermere</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-2">
                Your passport application requires two identical 2"×2" passport photos taken within the last 6 months. Popular passport photo locations near Windermere include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && photoLocations.length > 6 && (
                <button
                  onClick={() => setPhotoExpanded(true)}
                  className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors"
                >
                  <i className="ri-add-circle-line text-sm"></i>
                  Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://travel.state.gov/content/travel/en/passports/how-apply/photos.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>U.S. State Dept. Official Photo Requirements
                </a>
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          {/* Vital Records */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — Orange County</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records in Orange County. Most records can be ordered online for delivery within 5–10 business days.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                {[
                  { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Florida Department of Health in Orange County. Online, mail, or in-person options available.", url: "https://orange.floridahealth.gov/" },
                  { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Florida Department of Health in Orange County. Required for estate and legal matters.", url: "https://orange.floridahealth.gov/" },
                  { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Orange County Clerk of Courts. Certified copies available for name changes and legal purposes.", url: "https://www.myorangeclerk.com/" },
                  { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Orange County Clerk of Courts. Required for name change passport corrections and legal documentation.", url: "https://www.myorangeclerk.com/" },
                ].map((record, i) => (
                  <a
                    key={i}
                    href={record.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                      <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                      <p className="text-gray-600 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                      <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                        <i className="ri-external-link-line text-xs"></i>Get Records
                      </span>
                    </div>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-information-line text-amber-500 mr-1.5"></i>
                  <strong>Don&apos;t see your county?</strong> If the record you need was issued outside Orange County, visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
                </p>
              </div>
            </div>
          </div>

          {/* DMV */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver's License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Orange County Tax Collector — DMV Services</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your passport application. If you need a new or renewed Florida driver's license or REAL ID, the Orange County Tax Collector handles DMV services in the Windermere area.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                        <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                        <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://www.occompt.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>Orange County Tax Collector — DMV Services
                </a>
                <a href="https://www.flhsmv.gov/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>FLHSMV — Florida DMV
                </a>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-calendar-check-line text-amber-500 mr-1.5"></i>
                  <strong>Appointment recommended</strong> — walk-in wait times can be long. Book your slot online at the Orange County Tax Collector website before visiting.
                </p>
              </div>
            </div>
          </div>

          {/* Airports */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Windermere Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Windermere is served primarily by Orlando International Airport (MCO), one of the busiest airports in the United States and the gateway for Central Florida's massive international tourism and business travel economy. Orlando Sanford International (SFB) provides additional options for budget and charter carriers.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[
                  {
                    code: "MCO",
                    name: "Orlando International Airport",
                    distance: "Primary airport — ~25 miles east via Florida Turnpike / I-4",
                    address: "1 Jeff Fuqua Blvd, Orlando, FL 32827",
                    note: "One of the busiest U.S. airports with direct international service to London, Frankfurt, São Paulo, Toronto, Cancun, and dozens of Caribbean destinations. Serves American, Delta, JetBlue, Southwest, United, British Airways, Lufthansa, and international carriers. Most convenient for Windermere residents.",
                    flightUrl: "https://www.orlandoairports.net/",
                    flightLabel: "MCO Airport Website",
                    featured: true,
                  },
                  {
                    code: "SFB",
                    name: "Orlando Sanford International Airport",
                    distance: "~35 miles northeast via SR 417",
                    address: "1300 Red Cleveland Blvd, Sanford, FL 32773",
                    note: "Smaller airport serving Allegiant and select international charter carriers. Budget-friendly alternative for Windermere travelers with flexible schedules.",
                    flightUrl: "https://www.orlandosanfordairport.com/",
                    flightLabel: "SFB Airport Website",
                    featured: false,
                  },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>
                      )}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                      <i className="ri-road-map-line"></i>
                      <span>{airport.distance}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed mb-3">{airport.note}</p>
                    <a href={airport.flightUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>{airport.flightLabel}
                    </a>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your departure is within two weeks, standard processing won't make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>

          {/* Port Canaveral */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-ship-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Cruise Port</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Port Canaveral — Cruise Departures</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Port Canaveral is one of the busiest cruise ports in the world and the primary departure point for Windermere families embarking on Caribbean and Bahamas cruises. Located approximately 50–60 miles east of Windermere via the Beachline Expressway (SR 528), Port Canaveral serves Disney Cruise Line, Royal Caribbean, Carnival, Norwegian, and MSC.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                <div className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-4">
                  <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">Port Canaveral</p>
                  <p className="text-gray-500 text-xs">180 Christopher Columbus Dr, Cape Canaveral, FL 32920</p>
                  <p className="text-gray-400 text-xs mt-1">~50–60 miles east via SR 528 / Beachline Expressway</p>
                  <a href="https://www.portcanaveral.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors mt-3">
                    <i className="ri-external-link-line text-sm"></i>Port Canaveral Website
                  </a>
                </div>
                <div className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-4">
                  <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">Disney Cruise Line</p>
                  <p className="text-gray-500 text-xs">Terminal CT-8, Port Canaveral, FL 32920</p>
                  <p className="text-gray-400 text-xs mt-1">Disney's dedicated cruise terminal — extremely popular with Windermere families</p>
                  <a href="https://disneycruise.disney.go.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors mt-3">
                    <i className="ri-external-link-line text-sm"></i>Disney Cruise Line
                  </a>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-ship-line text-amber-500 mr-1.5"></i>
                  <strong>Cruise tip:</strong> Most cruise lines require at least six months of passport validity beyond your return date. Disney Cruise Line requires passports for most Caribbean and Bahamas itineraries. Verify all family members' passport expiration dates the moment you book — not when boarding documents arrive.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}